import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { FiSun, FiMoon } from "react-icons/fi";
import { themeActions } from "@/app/store";

/**
 * Theme manager:
 *  - Reads persisted preference from localStorage on mount.
 *  - Syncs Redux state -> <html data-theme="..."> and persists.
 *  - Exposes a small toggle button.
 */
export function ThemeManager() {
  const mode = useSelector((s) => s.theme.mode);
  const dispatch = useDispatch();

  // Hydrate from localStorage once
  useEffect(() => {
    try {
      const stored = localStorage.getItem("st_theme");
      if (stored && stored !== mode) {
        dispatch(themeActions.set(stored));
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push to DOM + persist on change
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    try {
      localStorage.setItem("st_theme", mode);
    } catch (e) {
      /* ignore */
    }
  }, [mode]);

  return null;
}

export default function ThemeToggle({ "data-testid": testId = "theme-toggle-btn" }) {
  const mode = useSelector((s) => s.theme.mode);
  const dispatch = useDispatch();
  const isDark = mode === "dark";

  return (
    <button
      data-testid={testId}
      onClick={() => dispatch(themeActions.toggle())}
      aria-label={`Switch to ${isDark ? "light" : "dark"} theme`}
      title={`Switch to ${isDark ? "light" : "dark"} theme`}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-[color:var(--border-strong)] bg-[color:var(--surface)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors overflow-hidden"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={mode}
          initial={{ y: -16, opacity: 0, rotate: -90 }}
          animate={{ y: 0, opacity: 1, rotate: 0 }}
          exit={{ y: 16, opacity: 0, rotate: 90 }}
          transition={{ duration: 0.25 }}
          className="inline-flex"
        >
          {isDark ? <FiSun size={15} /> : <FiMoon size={15} />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}
